import { useState } from 'react'
import { supabase } from '../../services/supabase'
import { useEntities } from '../../hooks/useEntities'
import { useAdvisors } from '../../hooks/useAdvisors'
import { Table } from '../../components/Common/Table'
import { Pagination } from '../../components/Common/Pagination'
import { Button } from '../../components/Common/Button'
import { EntityForm } from './EntityForm'
import { EntityImport } from './EntityImport'
import { EntityCodeMatcher } from './EntityCodeMatcher'

const TEMPLATE_HEADERS = [
  'Name', 'Type', 'Entity Number', 'Customer Code', 'Registration Number', 'Tax Number', 'Industry', 'Active',
  'Address Line 1', 'Address Line 2', 'Suburb', 'City', 'Postal Code', 'Country',
  'Contact Person', 'Phone', 'Email', 'Website',
  'Account Name', 'Account Type', 'Bank Name', 'Branch Name', 'Branch Code', 'Account Number',
]
const TEMPLATE_EXAMPLE = [
  'Example Corp', 'Company', 'ENT001', 'EXAM001', '2024/123456/07', '1234567890', 'Financial Services', 'TRUE',
  '123 Main Street', 'Suite 1', 'Sandton', 'Johannesburg', '2196', 'South Africa',
  'John Smith', '0110001234', 'info@example.com', 'www.example.com',
  'Example Corp', 'Cheque', 'Standard Bank', 'Sandton', '051001', '987654321',
]

const downloadTemplate = async () => {
  const XLSX = await import('xlsx')
  const wb = XLSX.utils.book_new()
  const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, TEMPLATE_EXAMPLE])
  ws['!cols'] = TEMPLATE_HEADERS.map(() => ({ wch: 20 }))
  XLSX.utils.book_append_sheet(wb, ws, 'Entities')
  XLSX.writeFile(wb, 'entities_template.xlsx')
}

const exportEntities = async () => {
  try {
    const { data, error } = await supabase
      .from('entities')
      .select('*, entity_addresses(*), entity_contacts(*), entity_banking_details(*)')
      .order('name')
    if (error) throw error

    const XLSX = await import('xlsx')
    const rows = data.map(e => {
      const addr    = e.entity_addresses?.[0]        || {}
      const contact = e.entity_contacts?.[0]         || {}
      const bank    = e.entity_banking_details?.[0]  || {}
      return [
        e.name                  || '',
        e.type                  || '',
        e.entity_num            || '',
        e.customer_code         || '',
        e.registration_number   || '',
        e.tax_number            || '',
        e.industry              || '',
        e.active ? 'Yes' : 'No',
        addr.address_line_1     || '',
        addr.address_line_2     || '',
        addr.suburb             || '',
        addr.city               || '',
        addr.postal_code        || '',
        addr.country            || '',
        contact.contact_person  || '',
        contact.phone           || '',
        contact.email           || '',
        contact.website         || '',
        bank.account_name       || '',
        bank.account_type       || '',
        bank.bank_name          || '',
        bank.branch_name        || '',
        bank.branch_code        || '',
        bank.account_number     || '',
      ]
    })

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, ...rows])
    ws['!cols'] = TEMPLATE_HEADERS.map(() => ({ wch: 22 }))
    XLSX.utils.book_append_sheet(wb, ws, 'Entities')
    XLSX.writeFile(wb, `entities_export_${new Date().toISOString().split('T')[0]}.xlsx`)
  } catch (err) {
    alert(`Export failed: ${err.message}`)
  }
}

export function EntitiesList({ sidebarCollapsed, isDarkMode = false }) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [selectedAdvisor, setSelectedAdvisor] = useState(null)
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState('asc')
  const [showForm, setShowForm] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [editingEntity, setEditingEntity] = useState(null)
  const [importResult, setImportResult] = useState(null)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [deleting, setDeleting] = useState(false)
  const [showDuplicateFinder, setShowDuplicateFinder] = useState(false)
  const [duplicateGroups, setDuplicateGroups] = useState([])
  const [duplicatesLoading, setDuplicatesLoading] = useState(false)
  const [selectedDuplicateIds, setSelectedDuplicateIds] = useState(new Set())
  const [showMatcher, setShowMatcher] = useState(false)
  const [relinking, setRelinking] = useState(false)
  const { advisors } = useAdvisors()
  const { entities, total, loading, createEntity, updateEntity, importEntities, relinkPoliciesByCode, refetch } = useEntities(page, 100, search, selectedAdvisor?.id)

  const handleSort = (key) => {
    setSortBy(prev => {
      if (prev === key) {
        setSortOrder(o => o === 'asc' ? 'desc' : 'asc')
        return prev
      }
      setSortOrder('asc')
      return key
    })
  }

  const sortedEntities = [...entities].sort((a, b) => {
    let aVal = a[sortBy] ?? ''
    let bVal = b[sortBy] ?? ''
    if (typeof aVal === 'boolean') return sortOrder === 'asc' ? (bVal ? 1 : -1) : (aVal ? 1 : -1)
    const cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' })
    return sortOrder === 'asc' ? cmp : -cmp
  })

  const handleRowClick = (row) => {
    setEditingEntity(row)
    setShowForm(true)
  }

  const handleSubmit = async (entityData, addressData, contactData, bankingData) => {
    if (editingEntity) {
      await updateEntity(editingEntity.id, entityData, addressData, contactData, bankingData)
    } else {
      await createEntity(entityData, addressData, contactData, bankingData)
    }
    setShowForm(false)
    setEditingEntity(null)
  }

  const handleImport = async (rows) => {
    try {
      const result = await importEntities(rows)
      const { created, updated } = result || {}
      setImportResult({ created: created || 0, updated: updated || 0 })
      setShowImport(false)
    } catch (err) {
      alert(`Import failed: ${err.message}`)
    }
  }

  const handleRelink = async () => {
    setRelinking(true)
    try {
      const count = await relinkPoliciesByCode()
      await refetch()
      alert(`Done — ${count} policies relinked to entities by Customer Code.`)
    } catch (err) {
      alert(`Relink failed: ${err.message}`)
    } finally {
      setRelinking(false)
    }
  }

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === entities.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(entities.map(e => e.id)))
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return

    const message = `Are you sure you want to delete ${selectedIds.size} ${selectedIds.size === 1 ? 'entity' : 'entities'}?\n\nThis action cannot be undone. Any policies linked to these entities will become orphaned.`

    if (!window.confirm(message)) return

    setDeleting(true)
    try {
      const idsToDelete = Array.from(selectedIds)
      const { error } = await supabase
        .from('entities')
        .delete()
        .in('id', idsToDelete)

      if (error) throw error

      setSelectedIds(new Set())
      // Refetch data
      window.location.reload()
    } catch (err) {
      alert(`Delete failed: ${err.message}`)
    } finally {
      setDeleting(false)
    }
  }

  const findDuplicates = async () => {
    setDuplicatesLoading(true)
    try {
      // Fetch all entities without pagination
      const { data: allEntities, error } = await supabase
        .from('entities')
        .select('id, name, tax_number, active, created_at')
        .order('name')

      if (error) throw error

      // Group by name
      const groups = {}
      allEntities.forEach(entity => {
        const key = entity.name?.toLowerCase() || 'unnamed'
        if (!groups[key]) {
          groups[key] = []
        }
        groups[key].push(entity)
      })

      // Filter to only show groups with 2+ entities
      const duplicates = Object.values(groups)
        .filter(group => group.length > 1)
        .sort((a, b) => b.length - a.length) // Most duplicates first

      setDuplicateGroups(duplicates)
      setShowDuplicateFinder(true)
    } catch (err) {
      alert(`Failed to find duplicates: ${err.message}`)
    } finally {
      setDuplicatesLoading(false)
    }
  }

  const toggleDuplicateSelect = (id) => {
    setSelectedDuplicateIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const handleDeleteDuplicates = async () => {
    if (selectedDuplicateIds.size === 0) return

    const message = `Delete ${selectedDuplicateIds.size} duplicate ${selectedDuplicateIds.size === 1 ? 'entity' : 'entities'}?\n\nThis action cannot be undone.`

    if (!window.confirm(message)) return

    setDeleting(true)
    try {
      const idsToDelete = Array.from(selectedDuplicateIds)
      const { error } = await supabase
        .from('entities')
        .delete()
        .in('id', idsToDelete)

      if (error) throw error

      // Refetch and clear
      setSelectedDuplicateIds(new Set())
      setShowDuplicateFinder(false)
      window.location.reload()
    } catch (err) {
      alert(`Delete failed: ${err.message}`)
    } finally {
      setDeleting(false)
    }
  }

  const columns = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          checked={entities.length > 0 && selectedIds.size === entities.length}
          onChange={toggleSelectAll}
          className="rounded"
        />
      ),
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedIds.has(row.id)}
          onChange={() => toggleSelect(row.id)}
          onClick={(e) => e.stopPropagation()}
          className="rounded"
        />
      )
    },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'type', label: 'Type', sortable: true },
    { key: 'entity_num', label: 'Entity Number', sortable: true },
    {
      key: 'customer_code',
      label: 'Cust. Code',
      sortable: true,
      render: (row) => row.customer_code
        ? <span className="font-mono text-xs font-semibold bg-sfp-orange/10 text-sfp-orange px-1.5 py-0.5 rounded">
            {row.customer_code}
          </span>
        : <span className="text-gray-300 text-xs">—</span>
    },
    {
      key: 'active',
      label: 'Active',
      sortable: true,
      render: (row) => (
        <span className={row.active ? 'badge-active' : 'badge-inactive'}>
          {row.active ? 'Yes' : 'No'}
        </span>
      )
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); handleRowClick(row) }}
          className="text-sfp-orange hover:text-orange-700 text-xs font-semibold px-2 py-1 rounded hover:bg-orange-50 transition"
        >
          Edit
        </button>
      )
    },
  ]

  return (
    <div className="p-6 space-y-6">
      {/* Import result banner */}
      {importResult && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded flex justify-between items-center text-sm">
          <span>
            {importResult.created > 0 && <span>{importResult.created} new {importResult.created === 1 ? 'entity' : 'entities'} created.</span>}
            {importResult.updated > 0 && <span className="ml-2">{importResult.updated} existing {importResult.updated === 1 ? 'entity' : 'entities'} updated.</span>}
            {importResult.created === 0 && importResult.updated === 0 && <span>No changes — file matched no records.</span>}
          </span>
          <button onClick={() => setImportResult(null)} className="text-green-600 hover:text-green-800 font-bold ml-4">✕</button>
        </div>
      )}

      {/* Action Bar */}
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          <Button variant="primary" onClick={() => { setEditingEntity(null); setShowForm(true) }}>
            + Add Entity
          </Button>
          <Button variant="secondary" onClick={() => setShowImport(true)}>
            Import Excel
          </Button>
          <Button variant="secondary" onClick={exportEntities}>
            ↓ Export Excel
          </Button>
          <Button variant="secondary" onClick={downloadTemplate}>
            Download Template
          </Button>
          <Button variant="secondary" onClick={() => setShowMatcher(true)}>
            🔗 Match Entity Codes
          </Button>
          <Button variant="secondary" onClick={handleRelink} disabled={relinking}>
            {relinking ? 'Relinking…' : '🔄 Relink Policies'}
          </Button>
          <Button
            variant="secondary"
            onClick={findDuplicates}
            disabled={duplicatesLoading}
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
          >
            {duplicatesLoading ? 'Scanning...' : '🔍 Find Duplicates'}
          </Button>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 ml-2 pl-2 border-l" style={{ borderColor: isDarkMode ? '#475569' : '#e5e7eb' }}>
              <span className="text-sm font-medium" style={{ color: isDarkMode ? '#e2e8f0' : '#333' }}>
                {selectedIds.size} selected
              </span>
              <Button
                variant="secondary"
                onClick={handleDeleteSelected}
                disabled={deleting}
                className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
              >
                {deleting ? 'Deleting…' : '🗑️ Delete Selected'}
              </Button>
            </div>
          )}
          <select
            value={selectedAdvisor?.id || ''}
            onChange={(e) => {
              const advisorId = e.target.value
              setSelectedAdvisor(advisorId ? advisors.find(a => a.id === advisorId) : null)
              setPage(1)
            }}
            className="px-3 py-2 bg-sfp-lightgray text-sfp-darkgray border border-sfp-border rounded text-sm transition hover:bg-sfp-white"
          >
            <option value="">All Advisors</option>
            {advisors.map(a => (
              <option key={a.id} value={a.id}>{a.full_name || a.email}</option>
            ))}
          </select>
        </div>
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field max-w-xs"
        />
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <Table
          columns={columns}
          data={sortedEntities}
          loading={loading}
          onRowClick={handleRowClick}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalItems={total}
        pageSize={100}
        onPageChange={setPage}
      />

      {/* Entity Form Modal */}
      {showForm && (
        <EntityForm
          onSubmit={handleSubmit}
          onClose={() => { setShowForm(false); setEditingEntity(null) }}
          initialData={editingEntity}
          sidebarCollapsed={sidebarCollapsed}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Import Modal */}
      {showImport && (
        <EntityImport
          onImport={handleImport}
          onClose={() => setShowImport(false)}
          sidebarCollapsed={sidebarCollapsed}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Entity Code Matcher Modal */}
      {showMatcher && (
        <EntityCodeMatcher
          onClose={() => setShowMatcher(false)}
          onDone={refetch}
          sidebarCollapsed={sidebarCollapsed}
        />
      )}

      {/* Duplicate Finder Modal */}
      {showDuplicateFinder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col" style={{ backgroundColor: isDarkMode ? '#1e293b' : '#ffffff' }}>
            {/* Header */}
            <div className="p-6 border-b" style={{ borderColor: isDarkMode ? '#475569' : '#e5e7eb' }}>
              <h3 className="text-lg font-bold flex justify-between items-center" style={{ color: isDarkMode ? '#f1f5f9' : '#333333' }}>
                <span>🔍 Find & Clean Duplicates</span>
                <button onClick={() => setShowDuplicateFinder(false)} className="text-2xl leading-none hover:opacity-70">✕</button>
              </h3>
              <p className="text-sm mt-2" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>
                Found {duplicateGroups.length} group{duplicateGroups.length === 1 ? '' : 's'} with duplicate entity names
              </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {duplicateGroups.length === 0 ? (
                <div className="text-center py-8" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>
                  No duplicates found! ✓
                </div>
              ) : (
                duplicateGroups.map((group, idx) => (
                  <div key={idx} className="border rounded-lg p-4" style={{ backgroundColor: isDarkMode ? '#0f172a' : '#f9fafb', borderColor: isDarkMode ? '#475569' : '#e5e7eb' }}>
                    <div className="font-semibold mb-3" style={{ color: isDarkMode ? '#f1f5f9' : '#333333' }}>
                      "{group[0].name}" ({group.length} duplicates)
                    </div>
                    <div className="space-y-2">
                      {group.map(entity => (
                        <label key={entity.id} className="flex items-center gap-3 p-2 rounded hover:bg-opacity-50" style={{ backgroundColor: selectedDuplicateIds.has(entity.id) ? (isDarkMode ? '#334155' : '#f0f9ff') : 'transparent' }}>
                          <input
                            type="checkbox"
                            checked={selectedDuplicateIds.has(entity.id)}
                            onChange={() => toggleDuplicateSelect(entity.id)}
                            className="w-4 h-4"
                          />
                          <div className="flex-1 text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#333333' }}>
                            <div className="font-medium">{entity.name}</div>
                            <div className="text-xs" style={{ color: isDarkMode ? '#94a3b8' : '#999999' }}>
                              {entity.tax_number && `Tax: ${entity.tax_number}`}
                              {entity.tax_number && ' • '}
                              {entity.active ? '✓ Active' : '○ Inactive'}
                              {' • '}
                              Created: {new Date(entity.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t flex gap-2 justify-between items-center" style={{ borderColor: isDarkMode ? '#475569' : '#e5e7eb' }}>
              <div className="text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>
                {selectedDuplicateIds.size > 0 && (
                  <span className="font-semibold" style={{ color: isDarkMode ? '#f1f5f9' : '#333333' }}>
                    {selectedDuplicateIds.size} selected for deletion
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDuplicateFinder(false)}
                  className="px-4 py-2 rounded text-sm font-semibold"
                  style={{ backgroundColor: isDarkMode ? '#334155' : '#f1f5f9', color: isDarkMode ? '#e2e8f0' : '#333333' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteDuplicates}
                  disabled={selectedDuplicateIds.size === 0 || deleting}
                  className="px-4 py-2 rounded text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? 'Deleting...' : `🗑️ Delete ${selectedDuplicateIds.size > 0 ? selectedDuplicateIds.size : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
